import { UserProfile } from 'fitbeat.gym-core';
import { IHeartRate } from 'fitbeat.models';
import React from 'react';
import { translate } from 'react-i18next';
import { KeyboardAvoidingView, Platform, StyleSheet, Text, View } from 'react-native';
import { verticalScale } from 'react-native-size-matters';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { Button, ButtonTypes } from '../../components';
import { getUrls } from '../../config';
import { UserProfileService } from '../../services/UserProfileService';
import theme, { ThemeType } from '../../theme';
import Theme from '../../theme';
import { BaseModal, TextInput } from '../../ui/components';
import { toggleHeartRateMonitorModalStudioVisible } from '../../ui/redux/uiActions';
import { UserDataConsumer } from '../../utils';
import { defaultScale } from '../../utils/scaling';
import {
  openStudioHRMInstructionsButtonPress,
  studioHRMInstructionsBackButtonPressed,
  UpdateHeartRateMonitorsFn,
  updateUserStudioHRM,
} from '../heartRateActions';
import { IAppStore } from '../reducer';
import {
  isSavingHeartRateIdentifierSelector,
  latestConnectedAppHRSelector,
  savingHeartRateIdentifierFailureSelector,
} from '../selectors';
import CurrentConnectedAppHeartRateBanner from './CurrentConnectedAppHeartRateBanner';
import { WebViewFaqWithBack } from './WebViewFaqWithBack';

interface IProps {
  userProfile: UserProfile;
  heartRateMonitorModalStudioVisible: boolean;
  isSavingHeartRateIdentifier: boolean;
  t: (key: string) => string;
  user?: {
    userProfile: UserProfile;
    userProfileService: UserProfileService;
  }
  hrmIdentifier: string;
  savingHeartRateIdentifierError: string | null;
  latestConnectedAppHR: IHeartRate | null;
  studioHRMSetupScreen: StudioHRMSetupScreen;
  initialize(initialValue: any): void;
  userUpdateFunc(): void;
  handleSubmit(func: (values: any) => Promise<void>): void;
}

interface IDispatchProps {
  saveHeartRateMonitorIdentifier(
    id: string,
    updateFunc: UpdateHeartRateMonitorsFn,
  ): void;
  toggleModal(visible: boolean): void;
  openInstructionsButtonPress(): void;
  instructionsBackButtonPress(): void;
}

export type StudioHRMSetupScreen = 'CaptureHRMId' | 'StudioHRMInstructions';

class HeartRateMonitorSetupStudioComponent extends React.PureComponent<
  IProps & IDispatchProps,
  any
> {
  public componentDidUpdate(prevProps: Readonly<IProps>) {
    const { heartRateMonitorModalStudioVisible } = this.props;
    if (prevProps.heartRateMonitorModalStudioVisible !== heartRateMonitorModalStudioVisible) {
      this.initializeValuesFromProps();
    }
  }

  public render() {
    const {
      heartRateMonitorModalStudioVisible,
      t,
      studioHRMSetupScreen,
    } = this.props;
    const faqVisible = studioHRMSetupScreen === 'StudioHRMInstructions';
    return (
      <BaseModal
        title={faqVisible ? t('homeWorkouts:howToConnect') : t('homeWorkouts:addHeartRateMonitor')}
        closeIconSize={35}
        modalContainer={[styles.modalContainer, faqVisible && { backgroundColor: Theme.BLACK }]}
        isModalVisible={heartRateMonitorModalStudioVisible}
        headerTextStyle={styles.headerTextStyle}
        headerTextColor={faqVisible && { color: Theme.GREY_MEDIUM_LIGHT }}
        closeModal={this.closeModal}
        containerStyle={styles.containerStyle}
        bodyStyle={{ backgroundColor: faqVisible ? Theme.BLACK : Theme.WHITE }}
      >
        {this.activeComponent()}
      </BaseModal>
    );
  }

  private activeComponent = () => {
    const { studioHRMSetupScreen } = this.props;
    if (studioHRMSetupScreen === 'CaptureHRMId') {
      return this.captureHRMId();
    }
    return this.studioHRMInstructions();
  };

  private captureHRMId = () => {
    const {
      t,
      isSavingHeartRateIdentifier,
      savingHeartRateIdentifierError,
      latestConnectedAppHR,
      openInstructionsButtonPress,
      hrmIdentifier,
    } = this.props;
    const showError = !!savingHeartRateIdentifierError;
    return (
      <>
        <View style={{flex: 1}}>
          <CurrentConnectedAppHeartRateBanner
            latestConnectedAppHR={latestConnectedAppHR}
            themeType={ThemeType.light}
            showFaq={openInstructionsButtonPress}
            t={t}
          />
          <Text style={[styles.descriptionText, { marginVertical: defaultScale(10) }]}>
            {t('formFields:whatYouCanDoWithYourHrm')}
          </Text>
          <Text style={styles.descriptionText}>
            {t('formFields:whereToFindYourHrm')}
          </Text>
          <Field
            forwardRef
            isRequired={true}
            name='hrmIdentifier'
            component={TextInput}
            placeholder={t('formFields:heartRateInputInstruction')}
            keyboardType='numeric'
            returnKeyType='done'
            autoFocus={true}
            mainContainerStyle={styles.inputContainerStyle}
            key={Platform.OS === 'web' && hrmIdentifier}
          />
          <Button
            title={'Save'}
            isLoading={isSavingHeartRateIdentifier}
            onPress={this.props.handleSubmit(this.onSubmit)}
            fontColor={Theme.WHITE}
            buttonType={ButtonTypes.NORMAL}
          />
          {showError && <Text style={styles.errorText}>{savingHeartRateIdentifierError}</Text>}
        </View>
        {Platform.OS !== 'web' &&
          <KeyboardAvoidingView
            behavior='position'
            keyboardVerticalOffset={verticalScale(45)}
            enabled={Platform.OS === 'ios'}
          >
            <Button
              title={t('heartRateDevice:compatibilityAndInstructions')}
              onPress={openInstructionsButtonPress}
              buttonType={ButtonTypes.OUTLINE}
            />
          </KeyboardAvoidingView>
        }
      </>
    );
  };

  private studioHRMInstructions = () => {
    const { STUDIO_HRM_INSTRUCTIONS } = getUrls();
    const { instructionsBackButtonPress } = this.props;
    return (
      <WebViewFaqWithBack url={STUDIO_HRM_INSTRUCTIONS} backFromFirstPageEvent={instructionsBackButtonPress} />
    );
  };

  private closeModal = () => {
    const { toggleModal } = this.props;
    toggleModal(false);
  };

  private initializeValuesFromProps = () => {
    const {
      initialize,
      user,
    } = this.props;
    if (user?.userProfile.heartRateMonitors?.studio) {
      initialize({ hrmIdentifier: user?.userProfile.heartRateMonitors?.studio });
    } else {
      initialize({ hrmIdentifier: '' });
    }
  };

  private onSubmit = async (values) => {
    const { hrmIdentifier } = values;
    const { user } = this.props;
    if (user) {
      await this.props.saveHeartRateMonitorIdentifier(
        hrmIdentifier.trim(),
        user.userProfileService.updateUserHeartRateMonitors,
      );
    }
    if (!this.props.savingHeartRateIdentifierError) {
      this.props.toggleModal(false);
    }
  }
}

const styles = StyleSheet.create({
  modalContainer: {
    backgroundColor: Theme.WHITE,
    flex: 1,
  },
  headerTextStyle: {
    zIndex: 1,
    borderBottomWidth: 1,
    borderBottomColor: Theme.GREY_MEDIUM_LIGHT,
  },
  containerStyle: {
    height: '100vh',
  },
  inputContainerStyle: {
    marginVertical: defaultScale(15),
  },
  descriptionText: {
    ...Theme.textStyle.bodyCopyReg,
  },
  errorText: {
    ...theme.textStyle.bodyCopyReg,
    marginTop: defaultScale(10),
    color: 'red',
  },
});

const mapStateToProps = (state: IAppStore) => {
  const { heartRateMonitorModalStudioVisible } = state.ui;
  return {
    heartRateMonitorModalStudioVisible,
    hrmIdentifier: formValueSelector('HeartRateMonitorInputStudio')(
      state,
      'hrmIdentifier',
    ),
    studioHRMSetupScreen: state.heartRateMonitor.studioHRMSetupScreen,
    isSavingHeartRateIdentifier: isSavingHeartRateIdentifierSelector(state),
    savingHeartRateIdentifierError: savingHeartRateIdentifierFailureSelector(state),
    latestConnectedAppHR: latestConnectedAppHRSelector(state),
  };
};

const mapDispatchToProps = (dispatch: any): IDispatchProps => {
  return {
    toggleModal: (visible: boolean) =>
      dispatch(toggleHeartRateMonitorModalStudioVisible(visible)),
    saveHeartRateMonitorIdentifier: (identifier, updateUserHeartRateMonitors) =>
      dispatch(updateUserStudioHRM(identifier, updateUserHeartRateMonitors)),
    openInstructionsButtonPress: () => dispatch(openStudioHRMInstructionsButtonPress()),
    instructionsBackButtonPress: () => dispatch(studioHRMInstructionsBackButtonPressed()),
  };
};

export const HeartRateMonitorSetupStudio = reduxForm({
  form: 'HeartRateMonitorInputStudio',
})(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(UserDataConsumer(translate(['formfields'], { wait: true })(HeartRateMonitorSetupStudioComponent))),
);
